// src/pages/RiskMapEditor.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    IconButton
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AddCircleOutline as AddIcon,
    PictureAsPdf as PdfIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

// --- Componente Interno RiskCanvasBlock ---
const RiskCanvasBlock = ({ title, content, risks, onAddRisk, onEditRisk, onDeleteRisk }) => {
    const getRiskColor = (severity) => {
        if (severity === 'high') return 'error.main';
        if (severity === 'medium') return 'warning.main';
        return 'success.main';
    };

    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0'
            }}
        >
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ borderBottom: '1px solid #ddd', pb: 1, fontSize: '1rem', fontWeight: 'bold' }}>
                    {title}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#666' }}>
                    {content || 'Nenhum conteúdo.'}
                </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box>
                {/* ALTERAÇÃO 1: A classe hide-on-pdf foi movida do Box para o Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Riscos Associados</Typography>
                    <Button className="hide-on-pdf" size="small" startIcon={<AddIcon />} onClick={onAddRisk} />
                </Box>
                <List dense sx={{ p: 0 }}>
                    {risks.length === 0 ? (<ListItem><ListItemText secondary="Nenhum risco adicionado." /></ListItem>) : (
                        risks.map(risk => (
                            <ListItem
                                key={risk.id}
                                dense
                                secondaryAction={
                                    <Box className="hide-on-pdf">
                                        <IconButton edge="end" aria-label="edit" onClick={() => onEditRisk(risk)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton edge="end" aria-label="delete" onClick={() => onDeleteRisk(risk.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <ListItemText
                                    primary={risk.description}
                                    secondary={`Severidade: ${risk.severity}`}
                                    secondaryTypographyProps={{ color: getRiskColor(risk.severity), fontWeight: 'bold' }}
                                />
                            </ListItem>
                        ))
                    )}
                </List>
            </Box>
        </Paper>
    );
};

// --- Componente RiskModal ---
const RiskModal = ({ open, onClose, onSubmit, blockName, initialData }) => {
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [probability, setProbability] = useState('medium');

    const isEditing = !!initialData;

    useEffect(() => {
        if (open) {
            if (isEditing) {
                setDescription(initialData.description);
                setSeverity(initialData.severity);
                setProbability(initialData.probability);
            } else {
                setDescription('');
                setSeverity('medium');
                setProbability('medium');
            }
        }
    }, [open, initialData, isEditing]);

    const handleSubmit = () => {
        if (!description.trim()) {
            alert("A descrição não pode estar vazia.");
            return;
        }
        onSubmit({ description, severity, probability });
    };

    if (!open) return null;

    return (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
            <Paper sx={{ p: 3, width: '90%', maxWidth: 500 }}>
                <Typography variant="h5" gutterBottom>
                    {isEditing ? `Editar Risco em "${blockName}"` : `Adicionar Risco a "${blockName}"`}
                </Typography>

                <TextField label="Descrição do Risco" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={3} margin="normal" />
                <FormControl fullWidth margin="normal"><InputLabel>Severidade</InputLabel><Select value={severity} label="Severidade" onChange={(e) => setSeverity(e.target.value)}><MenuItem value="low">Baixa</MenuItem><MenuItem value="medium">Média</MenuItem><MenuItem value="high">Alta</MenuItem></Select></FormControl>
                <FormControl fullWidth margin="normal"><InputLabel>Possibilidade</InputLabel><Select value={probability} label="Probabilidade" onChange={(e) => setProbability(e.target.value)}><MenuItem value="low">Baixa</MenuItem><MenuItem value="medium">Média</MenuItem><MenuItem value="high">Alta</MenuItem></Select></FormControl>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained">{isEditing ? 'Salvar Alterações' : 'Salvar Risco'}</Button>
                </Box>
            </Paper>
        </Box>
    );
};


// --- Componente Principal RiskMapEditor ---
const RiskMapEditor = () => {
    const { id } = useParams();
    const [canvas, setCanvas] = useState(null);
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentBlock, setCurrentBlock] = useState('');
    const [editingRisk, setEditingRisk] = useState(null);

    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const blockNames = { keyPartners: 'Parceiros-Chave', keyActivities: 'Atividades-Chave', keyResources: 'Recursos-Chave', valuePropositions: 'Proposta de Valor', customerRelationships: 'Relacionamento com Clientes', channels: 'Canais', customerSegments: 'Segmentos de Clientes', costStructure: 'Estrutura de Custos', revenueStreams: 'Fontes de Receita' };

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [canvasResponse, risksResponse] = await Promise.all([
                api.get(`/models/${id}`),
                api.get(`/canvases/${id}/risks`)
            ]);
            setCanvas(canvasResponse.data);
            setRisks(risksResponse.data);
        } catch (err) {
            setError('Não foi possível carregar os dados.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenAddModal = (blockName) => {
        setCurrentBlock(blockName);
        setEditingRisk(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (risk) => {
        setEditingRisk(risk);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRisk(null);
        setCurrentBlock('');
    };

    const handleSaveRisk = async (formData) => {
        if (editingRisk) {
            try {
                const response = await api.put(`/risks/${editingRisk.id}`, formData);
                setRisks(risks.map(risk => (risk.id === editingRisk.id ? response.data : risk)));
                handleCloseModal();
            } catch (error) {
                console.error('Erro ao atualizar risco:', error);
                alert('Falha ao atualizar o risco.');
            }
        } else {
            const newRiskData = { ...formData, blockName: currentBlock };
            try {
                const response = await api.post(`/canvases/${id}/risks`, newRiskData);
                setRisks((prevRisks) => [...prevRisks, response.data]);
                handleCloseModal();
            } catch (error) {
                console.error('Erro ao adicionar risco:', error);
                alert('Falha ao salvar o risco.');
            }
        }
    };

    const handleDeleteRisk = async (riskId) => {
        if (window.confirm('Tem certeza que deseja excluir este risco?')) {
            try {
                await api.delete(`/risks/${riskId}`);
                setRisks(risks.filter(risk => risk.id !== riskId));
            } catch (error) {
                console.error('Erro ao deletar risco:', error);
                alert('Falha ao excluir o risco.');
            }
        }
    };

    const handleGeneratePdf = async () => {
        setIsGeneratingPdf(true);
        const reportElement = document.getElementById('riskmap-container');
        const scaledContainer = reportElement.parentElement;
        const buttonsToHide = document.querySelectorAll('.hide-on-pdf');
        const originalContainerTransform = scaledContainer.style.transform;
        const originalButtonDisplays = Array.from(buttonsToHide).map(btn => btn.style.display);
        scaledContainer.style.transform = 'scale(1)';
        buttonsToHide.forEach(btn => { btn.style.display = 'none'; });
        const originalReportContainerBg = reportElement.style.backgroundColor;
        reportElement.style.backgroundColor = 'white';
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
            const canvasScreenshot = await html2canvas(reportElement, { scale: 2, logging: false, useCORS: true });
            const imgData = canvasScreenshot.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 40;
            const imgProps = pdf.getImageProperties(imgData);
            const aspectRatio = imgProps.height / imgProps.width;
            let imgWidth = pdfWidth - (margin * 2);
            let imgHeight = imgWidth * aspectRatio;
            const availableHeight = pdfHeight - margin; // Ajustado para dar mais espaço
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = imgHeight / aspectRatio;
            }
            const imgX = (pdfWidth - imgWidth) / 2;
            const imgY = margin;
            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);
            pdf.setFontSize(10);
            const dateStr = `Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')}`;
            pdf.text(dateStr, margin, pdfHeight - (margin / 2));
            pdf.save(`risk-map-${id}.pdf`);
        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            alert("Não foi possível gerar o relatório.");
        } finally {
            scaledContainer.style.transform = originalContainerTransform;
            buttonsToHide.forEach((btn, index) => { btn.style.display = originalButtonDisplays[index]; });
            reportElement.style.backgroundColor = originalReportContainerBg;
            setIsGeneratingPdf(false);
        }
    };

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ p: 4 }}>Erro: {error}</Typography>;
    if (!canvas) return <Typography sx={{ p: 4 }}>Canvas não encontrado.</Typography>;

    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <RiskModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSaveRisk}
                blockName={editingRisk ? blockNames[editingRisk.blockName] : blockNames[currentBlock]}
                initialData={editingRisk}
            />
            <Container maxWidth="xl" sx={{ py: 4, transform: 'scale(0.8)', transformOrigin: 'top center', transition: 'transform 0.2s ease-in-out' }}>
                
                {/* ALTERAÇÃO 2: O Box agora só tem os botões e o título foi removido daqui */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} className="hide-on-pdf">
                    <Button component={RouterLink} to="/riskmap" startIcon={<ArrowBackIcon />}>Voltar</Button>
                    <Button variant="outlined" color="secondary" size="large" startIcon={<PdfIcon />} onClick={handleGeneratePdf} disabled={isGeneratingPdf}>
                        {isGeneratingPdf ? 'Gerando...' : 'Gerar Relatório'}
                    </Button>
                </Box>

                {/* ALTERAÇÃO 2: O container do PDF agora inclui o título principal */}
                <Box id="riskmap-container" display="flex" flexDirection="column" gap={2} sx={{ backgroundColor: 'white', p: 3 }}>
                    
                    <Typography variant="h4" component="h1" textAlign="center" mb={3}>
                        {canvas.name} - Risk Map
                    </Typography>

                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><RiskCanvasBlock title="Key Partners" content={canvas.keyPartners} risks={risks.filter(r => r.blockName === 'keyPartners')} onAddRisk={() => handleOpenAddModal('keyPartners')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><RiskCanvasBlock title="Key Activities" content={canvas.keyActivities} risks={risks.filter(r => r.blockName === 'keyActivities')} onAddRisk={() => handleOpenAddModal('keyActivities')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                            <Box flex={1}><RiskCanvasBlock title="Key Resources" content={canvas.keyResources} risks={risks.filter(r => r.blockName === 'keyResources')} onAddRisk={() => handleOpenAddModal('keyResources')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                        </Box>
                        <Box flex={1}><RiskCanvasBlock title="Value Proposition" content={canvas.valuePropositions} risks={risks.filter(r => r.blockName === 'valuePropositions')} onAddRisk={() => handleOpenAddModal('valuePropositions')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><RiskCanvasBlock title="Customer Relationship" content={canvas.customerRelationships} risks={risks.filter(r => r.blockName === 'customerRelationships')} onAddRisk={() => handleOpenAddModal('customerRelationships')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                            <Box flex={1}><RiskCanvasBlock title="Channels" content={canvas.channels} risks={risks.filter(r => r.blockName === 'channels')} onAddRisk={() => handleOpenAddModal('channels')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                        </Box>
                        <Box flex={1}><RiskCanvasBlock title="Customer Segments" content={canvas.customerSegments} risks={risks.filter(r => r.blockName === 'customerSegments')} onAddRisk={() => handleOpenAddModal('customerSegments')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                    </Box>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><RiskCanvasBlock title="Cost Structure" content={canvas.costStructure} risks={risks.filter(r => r.blockName === 'costStructure')} onAddRisk={() => handleOpenAddModal('costStructure')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                        <Box flex={1}><RiskCanvasBlock title="Revenue Streams" content={canvas.revenueStreams} risks={risks.filter(r => r.blockName === 'revenueStreams')} onAddRisk={() => handleOpenAddModal('revenueStreams')} onEditRisk={handleOpenEditModal} onDeleteRisk={handleDeleteRisk} /></Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default RiskMapEditor;